'use client';

import * as React from 'react';
import { cn } from './utils';

type PopoverContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
};

type TriggerChildProps = {
  onClick?: (event: React.MouseEvent) => void;
  ref?: React.Ref<HTMLElement>;
};

const PopoverContext = React.createContext<PopoverContextValue>({
  open: false,
  setOpen: () => {},
  triggerRef: { current: null },
});

function Popover({
  open,
  onOpenChange,
  children,
}: {
  open?: boolean;
  onOpenChange?: (nextOpen: boolean) => void;
  children: React.ReactNode;
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = open !== undefined;
  const resolvedOpen = isControlled ? open : internalOpen;
  const setOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );
  const triggerRef = React.useRef<HTMLElement | null>(null);

  return (
    <PopoverContext.Provider value={{ open: resolvedOpen, setOpen, triggerRef }}>{children}</PopoverContext.Provider>
  );
}

function PopoverTrigger({
  children,
  asChild,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children: React.ReactNode;
  asChild?: boolean;
}) {
  const { open, setOpen, triggerRef } = React.useContext(PopoverContext);

  const attachRef = (node: HTMLElement | null) => {
    triggerRef.current = node;
  };

  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<TriggerChildProps>;
    return React.cloneElement(child, {
      ...props,
      ref: attachRef,
      onClick: (event: React.MouseEvent) => {
        child.props.onClick?.(event);
        if (!event.defaultPrevented) {
          setOpen(!open);
        }
      },
    } as TriggerChildProps);
  }

  return (
    <button type="button" ref={attachRef} onClick={() => setOpen(!open)} {...props}>
      {children}
    </button>
  );
}

function PopoverContent({
  className,
  sideOffset = 4,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
  children: React.ReactNode;
}) {
  const { open, triggerRef } = React.useContext(PopoverContext);
  const [position, setPosition] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });

  React.useEffect(() => {
    if (!open || !triggerRef.current) {
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + sideOffset,
      left: rect.left + rect.width / 2,
    });
  }, [open, sideOffset, triggerRef]);

  if (!open) {
    return null;
  }

  return (
    <div
      data-slot="popover-content"
      className={cn(
        'tw-bg-popover tw-text-popover-foreground tw-z-50 tw-w-72 tw-rounded-md tw-border tw-p-4 tw-shadow-md tw-outline-hidden',
        className,
      )}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        transform: 'translateX(-50%)',
      }}
      {...props}
    >
      {children}
    </div>
  );
}

function PopoverAnchor({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
